import { User, Trophy, Target, TrendingUp, BarChart3, ExternalLink } from 'lucide-react'
import { Link } from 'react-router-dom'

interface PlayerStatsData {
  user_id: string
  display_name: string
  total_games: number
  wins: number
  losses: number
  win_rate: number
  total_points_scored: number
  total_points_conceded: number
  goals_by_type: Record<string, number>
  role_breakdown: {
    defense: number
    attack: number
    full: number
  }
  recent_form: {
    last_10_games: number
    win_rate_last_10: number
  }
}

interface PlayerStatsProps {
  stats: PlayerStatsData
}

export function PlayerStats({ stats }: PlayerStatsProps) {
  const formatPercentage = (value: number) => `${(value * 100).toFixed(1)}%`
  const formatNumber = (value: number) => value.toLocaleString()

  const goalTypeLabels: Record<string, string> = {
    attack_shot: 'Attack Shots',
    midfield_shot: 'Midfield Shots',
    defense_shot: 'Defense Shots',
    goalkeeper_shot: 'Goalkeeper Shots',
    own_goal_attack: 'Own Goals (Attack)',
    own_goal_midfield: 'Own Goals (Midfield)',
    own_goal_defense: 'Own Goals (Defense)',
    own_goal_goalkeeper: 'Own Goals (Goalkeeper)',
    gamelle: 'Gamelle',
    monsterblock_defense: 'Monsterblock (Defense)',
    monsterblock_goalkeeper: 'Monsterblock (Goalkeeper)'
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-blue-100 rounded-full">
              <User className="h-8 w-8 text-blue-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{stats.display_name}</h2>
              <p className="text-gray-600">{stats.total_games} games played</p>
            </div>
          </div>
          <Link
            to={`/players/${stats.user_id}`}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            View Full Profile
            <ExternalLink className="h-4 w-4 ml-2" />
          </Link>
        </div>
      </div>

      {/* Key Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <Trophy className="h-8 w-8 text-yellow-500" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Win Rate</p>
              <p className="text-2xl font-bold text-gray-900">{formatPercentage(stats.win_rate)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <Target className="h-8 w-8 text-green-500" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Points Scored</p>
              <p className="text-2xl font-bold text-gray-900">{formatNumber(stats.total_points_scored)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <BarChart3 className="h-8 w-8 text-red-500" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Points Conceded</p>
              <p className="text-2xl font-bold text-gray-900">{formatNumber(stats.total_points_conceded)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <TrendingUp className="h-8 w-8 text-blue-500" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Recent Form</p>
              <p className="text-2xl font-bold text-gray-900">{formatPercentage(stats.recent_form.win_rate_last_10)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Role Breakdown */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Role Breakdown</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-gray-600">Defense</span>
              <span className="text-sm font-bold text-gray-900">{stats.role_breakdown.defense} games</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-gray-600">Attack</span>
              <span className="text-sm font-bold text-gray-900">{stats.role_breakdown.attack} games</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-gray-600">Full Table (1v1)</span>
              <span className="text-sm font-bold text-gray-900">{stats.role_breakdown.full} games</span>
            </div>
          </div>
        </div>

        {/* Goals by Type */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Goals by Type</h3>
          <div className="space-y-2">
            {Object.entries(stats.goals_by_type).map(([type, count]) => (
              <div key={type} className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-600">
                  {goalTypeLabels[type] || type.replace(/_/g, ' ')}
                </span>
                <span className="text-sm font-bold text-gray-900">{count}</span>
              </div>
            ))}
            {Object.keys(stats.goals_by_type).length === 0 && (
              <p className="text-sm text-gray-500 text-center py-4">No goals recorded yet</p>
            )}
          </div>
        </div>
      </div>

      {/* Game Record */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Game Record</h3>
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <p className="text-3xl font-bold text-green-600">{stats.wins}</p>
            <p className="text-sm text-gray-500">Wins</p>
          </div>
          <div>
            <p className="text-3xl font-bold text-red-600">{stats.losses}</p>
            <p className="text-sm text-gray-500">Losses</p>
          </div>
          <div>
            <p className="text-3xl font-bold text-blue-600">{stats.total_games}</p>
            <p className="text-sm text-gray-500">Total Games</p>
          </div>
        </div>
      </div>
    </div>
  )
}
